import React, { useState, useEffect, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { signInWithEmailAndPassword, createUserWithEmailAndPassword, signOut, onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { auth, db } from './firebase/firebaseConfig';
import Presentation from './components/presentation/Presentation';
import { 
  validateDeviceName, 
  validateEmail, 
  validatePassword,
  RateLimiter,
  SessionManager,
  getAuthErrorMessage,
  logSecurityEvent
} from './Utils/security';

function AuthenticationEnhanced({ project }) {
  // State management
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [deviceId, setDeviceId] = useState('');
  const [deviceName, setDeviceName] = useState('');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [showDeviceNameInput, setShowDeviceNameInput] = useState(false);
  const [isDeviceRegistered, setIsDeviceRegistered] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [errorStyle, setErrorStyle] = useState({});
  
  // Enhanced state for better UX
  const [isLoading, setIsLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [loginAttempts, setLoginAttempts] = useState(0);
  const [isRateLimited, setIsRateLimited] = useState(false);
  const [rateLimitTime, setRateLimitTime] = useState(0);
  const [isSignUpMode, setIsSignUpMode] = useState(false);
  
  // Security utilities
  const rateLimiter = useRef(new RateLimiter());
  const sessionManager = useRef(new SessionManager());

  useEffect(() => {
    // Check if deviceId exists in local storage
    const storedDeviceId = localStorage.getItem('deviceId');
    if (storedDeviceId) {
      setDeviceId(storedDeviceId);
    }

    // Check authentication state
    onAuthStateChanged(auth, (user) => {
      if (user) {
        checkDeviceActivation(user.email, true); // true = auto-authentication
      } else {
        setIsLoggedIn(false);
        sessionManager.current.clearSession();
      }
    });

    // Set up session management
    sessionManager.current.startSession(() => {
      handleLogout();
      setErrorMessage('Session expired. Please log in again.');
      setErrorStyle({
        backgroundColor: 'rgba(255, 165, 0, 0.2)',
        color: 'darkorange',
        padding: '10px',
        borderRadius: '5px',
        textAlign: 'center',
        marginTop: '10px',
      });
    });

    // Check for rate limiting on component mount
    checkRateLimit();

    return () => {
      sessionManager.current.clearSession();
    };
  }, []);

  // Handle rate limit countdown
  useEffect(() => {
    if (isRateLimited && rateLimitTime > 0) {
      const timer = setInterval(() => {
        setRateLimitTime(prev => {
          if (prev <= 1) {
            // Rate limit expired, reset everything
            setIsRateLimited(false);
            setLoginAttempts(0);
            setErrorMessage('');
            setErrorStyle({});
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [isRateLimited, rateLimitTime]);

  const checkRateLimit = () => {
    const identifier = username || 'anonymous';
    if (rateLimiter.current.isRateLimited(identifier)) {
      setIsRateLimited(true);
      const remainingTime = rateLimiter.current.getRemainingTime(identifier);
      setRateLimitTime(Math.ceil(remainingTime / 1000));
      
      // Update countdown
      const interval = setInterval(() => {
        const timeLeft = rateLimiter.current.getRemainingTime(identifier);
        if (timeLeft <= 0) {
          setIsRateLimited(false);
          setRateLimitTime(0);
          clearInterval(interval);
        } else {
          setRateLimitTime(Math.ceil(timeLeft / 1000));
        }
      }, 1000);
    }
  };

  const validateInputs = () => {
    const errors = {};
    
    // Validate email
    const emailValidation = validateEmail(username);
    if (!emailValidation.isValid) {
      errors.username = emailValidation.error;
    }
    
    // Validate password
    const passwordValidation = validatePassword(password);
    if (!passwordValidation.isValid) {
      errors.password = passwordValidation.error;
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const checkDeviceActivation = async (email, isAutoAuth = false) => {
    try {
      const userRef = doc(db, `projects/${project}/users`, email);
      const userDoc = await getDoc(userRef);

      if (userDoc.exists()) {
        const devices = userDoc.data().devices;
        if (devices && Object.values(devices).some(device => device.deviceId === deviceId)) {
          const deviceNameRegistered = Object.keys(devices).find(key => devices[key].deviceId === deviceId);
          setDeviceName(deviceNameRegistered);
          setIsDeviceRegistered(true);
          const deviceData = devices[deviceNameRegistered];
          if (deviceData.activated) {
            setIsLoggedIn(true);
            sessionManager.current.resetSession();
            await logSecurityEvent('successful_login', { deviceName: deviceNameRegistered }, db, auth);
          } else {
            setErrorMessage(`This device (${deviceNameRegistered}) has not been activated, please contact Admin.`);
            setErrorStyle({
              backgroundColor: 'rgba(255, 165, 0, 0.2)',
              color: 'darkorange',
              padding: '10px',
              borderRadius: '5px',
              textAlign: 'center',
              marginTop: '10px',
            });
            await logSecurityEvent('device_not_activated', { deviceName: deviceNameRegistered }, db, auth);
            handleLogout();
          }
        } else {
          // Device not registered
          if (isAutoAuth) {
            // During auto-authentication, just log and don't show registration
            console.log('Device not registered for user during auto-auth:', email);
          } else {
            // During manual sign-in, show device registration
            setShowDeviceNameInput(true);
          }
        }
      }
    } catch (error) {
      console.error('Error checking device activation:', error);
      await logSecurityEvent('device_check_error', { error: error.message }, db, auth);
    }
  };

  const handleSignUp = async () => {
    // Clear previous errors
    setErrorMessage('');
    setErrorStyle({});
    setValidationErrors({});
    
    // Validate inputs
    if (!validateInputs()) {
      return;
    }

    // Check rate limiting
    if (isRateLimited) {
      setErrorMessage(`Too many attempts. Please wait ${rateLimitTime} seconds before trying again.`);
      setErrorStyle({
        backgroundColor: 'rgba(255, 0, 0, 0.2)',
        color: 'red',
        padding: '10px',
        borderRadius: '5px',
        textAlign: 'center',
        marginTop: '10px',
      });
      return;
    }

    setIsLoading(true);

    try {
      // Record signup attempt
      rateLimiter.current.recordAttempt(username);
      setLoginAttempts(prev => prev + 1);

      // Create user account
      const userCredential = await createUserWithEmailAndPassword(auth, username, password);
      const user = userCredential.user;
      
      // Create user document in Firestore
      await setDoc(doc(db, `projects/${project}/users`, username), {
        email: username,
        createdAt: new Date().toISOString(),
        lastLogin: new Date().toISOString(),
        devices: {},
        isActive: true
      });

      // Generate device ID for new user
      const newDeviceId = uuidv4();
      setDeviceId(newDeviceId);
      localStorage.setItem('deviceId', newDeviceId);

      // Show device registration for new users
      setShowDeviceNameInput(true);
      setIsSignUpMode(false);
      
      // Log security event
      await logSecurityEvent('signup_success', { 
        email: username, 
        deviceId: newDeviceId 
      }, db, auth);
      
    } catch (error) {
      console.error('Signup error:', error);
      
      // Log security event
      await logSecurityEvent('signup_failed', { 
        email: username, 
        error: error.message 
      }, db, auth);

      const errorMsg = getAuthErrorMessage(error.code);
      setErrorMessage(errorMsg);
      setErrorStyle({
        backgroundColor: 'rgba(255, 0, 0, 0.2)',
        color: 'red',
        padding: '10px',
        borderRadius: '5px',
        textAlign: 'center',
        marginTop: '10px',
      });
      
      setLoginAttempts(prev => prev + 1);
      if (loginAttempts >= 4) {
        setIsRateLimited(true);
        setRateLimitTime(300); // 5 minutes
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = async () => {
    // Clear previous errors
    setErrorMessage('');
    setErrorStyle({});
    setValidationErrors({});
    
    // Validate inputs
    if (!validateInputs()) {
      return;
    }

    // Check rate limiting
    if (isRateLimited) {
      setErrorMessage(`Too many login attempts. Please wait ${rateLimitTime} seconds before trying again.`);
      setErrorStyle({
        backgroundColor: 'rgba(255, 0, 0, 0.2)',
        color: 'red',
        padding: '10px',
        borderRadius: '5px',
        textAlign: 'center',
        marginTop: '10px',
      });
      return;
    }

    setIsLoading(true);

    try {
      // Record login attempt
      rateLimiter.current.recordAttempt(username);
      setLoginAttempts(prev => prev + 1);

      // Sign in the user
      await signInWithEmailAndPassword(auth, username, password);

      // Generate a new device ID if one does not exist
      if (!deviceId) {
        const newDeviceId = uuidv4();
        setDeviceId(newDeviceId);
        localStorage.setItem('deviceId', newDeviceId);
      }

      // Reference to the user document under the project
      const userRef = doc(db, `projects/${project}/users`, username);
      const userDoc = await getDoc(userRef);

      const currentLocation = window.location.href;
      const currentDate = new Date().toISOString();

      if (!userDoc.exists()) {
        // User document doesn't exist, prompt for device name
        setShowDeviceNameInput(true);
        await logSecurityEvent('new_user_login', { email: username }, db, auth);
      } else {
        // Check if the device is already registered
        const devices = userDoc.data().devices;
        if (!devices || !Object.values(devices).some(device => device.deviceId === deviceId)) {
          // Device is not registered, prompt for device name
          setShowDeviceNameInput(true);
          await logSecurityEvent('new_device_login', { email: username }, db, auth);
        } else {
          // Device is already registered
          const deviceNameRegistered = Object.keys(devices).find(key => devices[key].deviceId === deviceId);
          setDeviceName(deviceNameRegistered);
          setIsDeviceRegistered(true);
          const deviceData = devices[deviceNameRegistered];
          if (deviceData.activated) {
            // Update the last login date
            await updateDoc(userRef, {
              [`devices.${deviceNameRegistered}.lastLogin`]: currentDate,
            });
            setIsLoggedIn(true);
            sessionManager.current.resetSession();
            await logSecurityEvent('successful_login', { deviceName: deviceNameRegistered }, db, auth);
          } else {
            setErrorMessage(`This device (${deviceNameRegistered}) has not been activated, please contact Admin.`);
            setErrorStyle({
              backgroundColor: 'rgba(255, 165, 0, 0.2)',
              color: 'darkorange',
              padding: '10px',
              borderRadius: '5px',
              textAlign: 'center',
              marginTop: '10px',
            });
            await logSecurityEvent('device_not_activated', { deviceName: deviceNameRegistered }, db, auth);
            handleLogout();
          }
        }
      }
    } catch (error) {
      console.error('Login failed:', error);
      const errorMsg = getAuthErrorMessage(error);
      setErrorMessage(errorMsg);
      setErrorStyle({
        backgroundColor: 'rgba(255, 0, 0, 0.2)',
        color: 'red',
        padding: '10px',
        borderRadius: '5px',
        textAlign: 'center',
        marginTop: '10px',
      });
      await logSecurityEvent('login_failed', { error: error.code, message: error.message }, db, auth);
      checkRateLimit();
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegisterDevice = async () => {
    // Validate device name
    const deviceValidation = validateDeviceName(deviceName);
    if (!deviceValidation.isValid) {
      setValidationErrors({ deviceName: deviceValidation.error });
      return;
    }

    setIsLoading(true);

    try {
      const userRef = doc(db, `projects/${project}/users`, username);
      const userDoc = await getDoc(userRef);

      const currentLocation = window.location.href;
      const currentDate = new Date().toISOString();

      if (!userDoc.exists()) {
        // Create user document with the current device name and device ID
        await setDoc(userRef, {
          devices: {
            [deviceName]: {
              deviceId,
              activated: false,
              dateAdded: currentDate,
              lastLogin: currentDate,
              locationAdded: currentLocation,
            },
          },
        });
        await logSecurityEvent('device_registered', { deviceName, email: username }, db, auth);
      } else {
        // Check if the device ID is already registered
        const devices = userDoc.data().devices;
        if (devices && Object.values(devices).some(device => device.deviceId === deviceId)) {
          setErrorMessage('This device is already registered under your account.');
          setErrorStyle({
            backgroundColor: 'rgba(255, 0, 0, 0.2)',
            color: 'red',
            padding: '10px',
            borderRadius: '5px',
            textAlign: 'center',
            marginTop: '10px',
          });
          handleLogout();
          return;
        }

        // Update the user document to add the new device
        await updateDoc(userRef, {
          [`devices.${deviceName}`]: {
            deviceId,
            activated: false,
            dateAdded: currentDate,
            lastLogin: currentDate,
            locationAdded: currentLocation,
          },
        });
        await logSecurityEvent('device_registered', { deviceName, email: username }, db, auth);
      }

      setIsDeviceRegistered(true);
      setErrorMessage(`This device (${deviceName}) has not been activated, please contact Admin.`);
      setErrorStyle({
        backgroundColor: 'rgba(255, 165, 0, 0.2)',
        color: 'darkorange',
        padding: '10px',
        borderRadius: '5px',
        textAlign: 'center',
        marginTop: '10px',
      });
      handleLogout();
    } catch (error) {
      console.error('Device registration failed:', error);
      setErrorMessage('Failed to register device. Please try again.');
      setErrorStyle({
        backgroundColor: 'rgba(255, 0, 0, 0.2)',
        color: 'red',
        padding: '10px',
        borderRadius: '5px',
        textAlign: 'center',
        marginTop: '10px',
      });
      await logSecurityEvent('device_registration_failed', { error: error.message }, db, auth);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    signOut(auth);
    setIsLoggedIn(false);
    sessionManager.current.clearSession();
    setLoginAttempts(0);
    setIsRateLimited(false);
    setRateLimitTime(0);
    setShowDeviceNameInput(false); // Hide device registration on logout
    setIsDeviceRegistered(false);
  };

  const handleInputChange = (setter) => (event) => {
    setter(event.target.value);
    // Clear device name input and hide register device button when username or password changes
    setDeviceName('');
    setShowDeviceNameInput(false);
    setIsDeviceRegistered(false);
    setErrorMessage('');
    setErrorStyle({});
    setValidationErrors({});
  };

  const handleUsernameChange = (event) => {
    const value = event.target.value;
    setUsername(value);
    
    // Clear previous errors
    setErrorMessage('');
    setErrorStyle({});
    setValidationErrors(prev => ({ ...prev, username: '' }));
    
    // Real-time validation for visual feedback
    if (value.length > 0) {
      const emailValidation = validateEmail(value);
      if (emailValidation.isValid) {
        // Valid email - will show green border
      } else {
        // Invalid email - will show red border
      }
    }
  };

  const handlePasswordChange = (event) => {
    const value = event.target.value;
    setPassword(value);
    
    // Clear previous errors
    setErrorMessage('');
    setErrorStyle({});
    setValidationErrors(prev => ({ ...prev, password: '' }));
    
    // Real-time validation for visual feedback
    if (value.length > 0) {
      const passwordValidation = validatePassword(value);
      if (passwordValidation.isValid) {
        // Valid password - will show green border
      } else {
        // Invalid password - will show red border
      }
    }
  };

  const handleDeviceNameChange = (event) => {
    setDeviceName(event.target.value);
    // Clear validation errors when user starts typing
    if (validationErrors.deviceName) {
      setValidationErrors(prev => ({ ...prev, deviceName: '' }));
    }
  };

  // Helper function to get input styling based on validation state
  const getInputStyling = (fieldName, value) => {
    if (!value || value.length === 0) {
      // Empty field - default styling
      return 'border-white/20 focus:border-yellow-500/50 focus:ring-yellow-500/50';
    }
    
    if (validationErrors[fieldName]) {
      // Has validation error - red styling
      return 'border-red-500/50 focus:ring-red-500/50';
    }
    
    // Check if field is valid for green styling
    let isValid = false;
    if (fieldName === 'username') {
      isValid = validateEmail(value).isValid;
    } else if (fieldName === 'password') {
      isValid = validatePassword(value).isValid;
    } else if (fieldName === 'deviceName') {
      isValid = validateDeviceName(value).isValid;
    }
    
    if (isValid) {
      // Valid field - green styling
      return 'border-green-500/50 focus:ring-green-500/50';
    } else {
      // Invalid field - red styling
      return 'border-red-500/50 focus:ring-red-500/50';
    }
  };

  // Helper function to get validation icon
  const getValidationIcon = (fieldName, value) => {
    if (!value || value.length === 0) {
      return null; // No icon for empty fields
    }
    
    if (validationErrors[fieldName]) {
      // Has validation error - show X icon
      return (
        <div className="absolute top-1/2 right-3 transform -translate-y-1/2 pointer-events-none">
          <svg className="h-5 w-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </div>
      );
    }
    
    // Check if field is valid for green checkmark
    let isValid = false;
    if (fieldName === 'username') {
      isValid = validateEmail(value).isValid;
    } else if (fieldName === 'password') {
      isValid = validatePassword(value).isValid;
    } else if (fieldName === 'deviceName') {
      isValid = validateDeviceName(value).isValid;
    }
    
    if (isValid) {
      // Valid field - show checkmark
      return (
        <div className="absolute top-1/2 right-3 transform -translate-y-1/2 pointer-events-none">
          <svg className="h-5 w-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
        </div>
      );
    } else {
      // Invalid field - show X icon
      return (
        <div className="absolute top-1/2 right-3 transform -translate-y-1/2 pointer-events-none">
          <svg className="h-5 w-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </div>
      );
    }
  };

  // Handle Enter key press for form submission
  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      if (showDeviceNameInput && !isDeviceRegistered) {
        handleRegisterDevice();
      } else if (isSignUpMode) {
        handleSignUp();
      } else {
        handleLogin();
      }
    }
  };

  // Activity tracking for session management
  useEffect(() => {
    const handleActivity = () => {
      if (isLoggedIn) {
        sessionManager.current.resetSession();
      }
    };

    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
    events.forEach(event => {
      document.addEventListener(event, handleActivity, true);
    });

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleActivity, true);
      });
    };
  }, [isLoggedIn]);

  if (isLoggedIn) {
    return <Presentation userEmail={username} onLogout={handleLogout} />;
  }

  return (
    <div className="relative min-h-screen bg-gradient-to-br from-slate-900 via-gray-900 to-black overflow-hidden" style={{ minHeight: '100vh', minHeight: '100dvh', height: '100vh', height: '100dvh' }}>
      {/* Animated background elements */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-yellow-500/10 via-transparent to-yellow-600/5"></div>
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-yellow-500/5 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-yellow-400/5 rounded-full blur-3xl animate-pulse delay-1000"></div>
      </div>
      
      {/* Grid pattern overlay */}
      <div className="absolute inset-0 opacity-20">
        <div className="w-full h-full" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23fbbf24' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          backgroundSize: '60px 60px'
        }}></div>
      </div>
      
      <div className="relative z-10 flex min-h-screen flex-col lg:flex-row" style={{ minHeight: '100vh', minHeight: '100dvh', height: '100vh', height: '100dvh' }}>
        {/* Left half - Hero section with image */}
        <div className="hidden lg:flex lg:w-1/2 relative">
          <div className="absolute inset-0 bg-gradient-to-br from-yellow-500/20 to-yellow-600/10"></div>
          <div className="relative w-full h-full flex items-center justify-center p-12">
            <div className="text-center space-y-8">
              <div className="relative">
                <div className="absolute -inset-4 bg-yellow-500/20 rounded-full blur-xl"></div>
                <div className="relative w-32 h-32 mx-auto bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-full flex items-center justify-center shadow-2xl">
                  <span className="text-4xl font-black text-gray-900">3DMA</span>
                </div>
              </div>
              <div className="space-y-4">
                <h1 className="text-5xl font-bold text-white leading-tight">
                  <span className="bg-gradient-to-r from-yellow-400 to-yellow-600 bg-clip-text text-transparent">
                    PICKSTONE
                  </span>
                </h1>
                <h2 className="text-2xl text-yellow-200 font-light">
                  MINING INTELLIGENCE PLATFORM
                </h2>
                <p className="text-gray-300 text-lg max-w-md mx-auto leading-relaxed">
                  Advanced 3D visualization and analysis for modern mining operations
                </p>
              </div>
              <div className="flex justify-center space-x-4">
                <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></div>
                <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse delay-300"></div>
                <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse delay-700"></div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Right half - Login form */}
        <div className="w-full lg:w-1/2 flex items-center justify-center p-4 sm:p-6 lg:p-12" style={{ minHeight: '100vh', minHeight: '100dvh', height: '100vh', height: '100dvh' }}>
          <div className="w-full max-w-md mx-auto">
            {/* Mobile logo */}
            <div className="lg:hidden flex justify-center mb-6 sm:mb-8">
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-full flex items-center justify-center shadow-xl">
                <span className="text-xl sm:text-2xl font-black text-gray-900">3DMA</span>
              </div>
            </div>
            
            {/* Login form container */}
            <div className="relative">
              <div className="absolute -inset-1 bg-gradient-to-r from-yellow-500/20 to-yellow-600/20 rounded-2xl blur"></div>
              <div className="relative bg-white/10 backdrop-blur-xl rounded-2xl border border-white/20 shadow-2xl p-4 sm:p-6 lg:p-8">
                <div className="text-center mb-4 sm:mb-6">
                  <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">
                    {isSignUpMode ? 'Create Account' : 'Welcome Back'}
                  </h2>
                  <p className="text-sm sm:text-base text-gray-300">
                    {isSignUpMode ? 'Sign up to get started with your mining dashboard' : 'Sign in to access your mining dashboard'}
                  </p>
                </div>
                <div className="space-y-3 sm:space-y-4">
                  {/* Username field */}
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-300 mb-2">Email Address</label>
                    <div className="relative">
                      <input
                        type="email"
                        placeholder="Enter your email"
                        value={username}
                        onChange={handleUsernameChange}
                        onKeyDown={handleKeyPress}
                        className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-white/5 border border-white/20 rounded-xl focus:outline-none focus:ring-2 focus:ring-yellow-500/50 text-white placeholder-gray-400 transition-all duration-300 backdrop-blur-sm text-sm sm:text-base ${getInputStyling('username', username)}`}
                        disabled={isLoading}
                      />
                      {getValidationIcon('username', username)}
                    </div>
                    {validationErrors.username && (
                      <p className="text-red-400 text-xs sm:text-sm mt-2 flex items-center">
                        <svg className="w-3 h-3 sm:w-4 sm:h-4 mr-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        {validationErrors.username}
                      </p>
                    )}
                  </div>

                  {/* Password field */}
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-300 mb-2">Password</label>
                    <div className="relative">
                      <input
                        type="password"
                        placeholder="Enter your password"
                        value={password}
                        onChange={handlePasswordChange}
                        onKeyDown={handleKeyPress}
                        className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-white/5 border border-white/20 rounded-xl focus:outline-none focus:ring-2 focus:ring-yellow-500/50 text-white placeholder-gray-400 transition-all duration-300 backdrop-blur-sm text-sm sm:text-base ${getInputStyling('password', password)}`}
                        disabled={isLoading}
                      />
                      {getValidationIcon('password', password)}
                    </div>
                    {validationErrors.password && (
                      <p className="text-red-400 text-xs sm:text-sm mt-2 flex items-center">
                        <svg className="w-3 h-3 sm:w-4 sm:h-4 mr-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        {validationErrors.password}
                      </p>
                    )}
                  </div>
                  {/* Login/Signup button */}
                  <button
                    onClick={isSignUpMode ? handleSignUp : handleLogin}
                    disabled={isLoading || isRateLimited}
                    className={`w-full py-2.5 sm:py-3 px-4 sm:px-6 rounded-xl font-semibold text-sm sm:text-base transition-all duration-300 transform ${
                      isLoading || isRateLimited
                        ? 'bg-gray-500/50 cursor-not-allowed text-gray-400'
                        : 'bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-400 hover:to-yellow-500 text-gray-900 hover:shadow-2xl hover:shadow-yellow-500/25 hover:-translate-y-1 focus:ring-2 focus:ring-yellow-500/50'
                    }`}
                  >
                    {isLoading ? (
                      <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-4 w-4 sm:h-5 sm:w-5 border-2 border-gray-900 border-t-transparent mr-2 sm:mr-3"></div>
                        <span className="text-xs sm:text-sm">
                          {isSignUpMode ? 'Creating Account...' : 'Authenticating...'}
                        </span>
                      </div>
                    ) : isRateLimited ? (
                      <div className="flex items-center justify-center">
                        <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-2 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span className="text-xs sm:text-sm">Wait {rateLimitTime}s</span>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center">
                        <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                        </svg>
                        <span className="text-xs sm:text-sm">
                          {isSignUpMode ? 'Create Account' : 'Sign In'}
                        </span>
                      </div>
                    )}
                  </button>
                  
                  {/* Toggle between Sign In and Sign Up */}
                  <div className="text-center mt-4">
                    <p className="text-sm text-gray-400">
                      {isSignUpMode ? 'Already have an account?' : "Don't have an account?"}
                    </p>
                    <button
                      onClick={() => {
                        setIsSignUpMode(!isSignUpMode);
                        setErrorMessage('');
                        setErrorStyle({});
                        setValidationErrors({});
                      }}
                      className="text-yellow-400 hover:text-yellow-300 font-medium text-sm transition-colors duration-200 mt-1"
                    >
                      {isSignUpMode ? 'Sign In' : 'Create Account'}
                    </button>
                  </div>
                  
                  {/* Device registration section */}
                  {showDeviceNameInput && (
                    <div className="mt-3 sm:mt-4 p-3 sm:p-4 bg-white/5 border border-white/10 rounded-xl">
                      <div className="text-center mb-3">
                        <div className="w-8 h-8 sm:w-10 sm:h-10 mx-auto bg-yellow-500/20 rounded-full flex items-center justify-center mb-2">
                          <svg className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                          </svg>
                        </div>
                        <h3 className="text-sm sm:text-base font-semibold text-white mb-1">Device Registration</h3>
                        <p className="text-xs text-gray-400">Register this device for secure access</p>
                      </div>
                      
                      <div className="space-y-2 sm:space-y-3">
                        <div className="relative">
                          <label className="block text-xs sm:text-sm font-medium text-gray-300 mb-1">Device Name</label>
                          <div className="relative">
                            <input
                              type="text"
                              placeholder="Enter device name"
                              value={deviceName}
                              onChange={handleDeviceNameChange}
                              onKeyDown={handleKeyPress}
                              className={`w-full px-3 py-2 bg-white/5 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500/50 text-white placeholder-gray-400 transition-all duration-300 backdrop-blur-sm text-sm ${getInputStyling('deviceName', deviceName)}`}
                              disabled={isDeviceRegistered || isLoading}
                            />
                            {getValidationIcon('deviceName', deviceName)}
                          </div>
                          {validationErrors.deviceName && (
                            <p className="text-red-400 text-xs mt-1 flex items-center">
                              <svg className="w-3 h-3 mr-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                              </svg>
                              {validationErrors.deviceName}
                            </p>
                          )}
                        </div>
                        
                        {!isDeviceRegistered && (
                          <button
                            onClick={handleRegisterDevice}
                            disabled={isLoading}
                            className={`w-full py-2 px-4 rounded-lg font-semibold text-sm transition-all duration-300 transform ${
                              isLoading
                                ? 'bg-gray-500/50 cursor-not-allowed text-gray-400'
                                : 'bg-gradient-to-r from-green-500 to-green-600 hover:from-green-400 hover:to-green-500 text-white hover:shadow-2xl hover:shadow-green-500/25 hover:-translate-y-1 focus:ring-2 focus:ring-green-500/50'
                            }`}
                          >
                            {isLoading ? (
                              <div className="flex items-center justify-center">
                                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                                <span className="text-xs">Registering...</span>
                              </div>
                            ) : (
                              <div className="flex items-center justify-center">
                                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                </svg>
                                <span className="text-xs">Register Device</span>
                              </div>
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                  {/* Error message */}
                  {errorMessage && (
                    <div className="mt-4 p-3 sm:p-4 bg-red-500/10 border border-red-500/20 rounded-xl">
                      <div className="flex items-center">
                        <svg className="w-4 h-4 sm:w-5 sm:h-5 text-red-400 mr-2 sm:mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                        <p className="text-red-300 text-xs sm:text-sm font-medium">{errorMessage}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AuthenticationEnhanced;
